import { VisibilityState } from "@tanstack/react-table";
import { startOfDay, subWeeks } from "date-fns";
import { DateRange } from "react-day-picker";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export interface DatePickerRange extends DateRange {
  from: Date;
  to: Date;
}

export enum TableIds {
  Commers_Review = "commers-review",
  Commers_Stock = "commers-stock",
  Commers_Profit = "commers-profit",
  Commers_Orders = "commers-orders",
  Commers_OrdersCard = "commers-order-card",
  Commers_PriceCard = "commers-price-card",
  Commers_RatingCard = "commers-rating-card",
  Commers_ConversionCard = "commers-conversion-card",
  Commers_AdsCard = "commers-ads-card",
  Commers_EconomicCard = "commers-economic-card",
  Commers_SummaryCard = "commers-summary-card",
  Commers_ReportCard = "commers-report-card",
  Services_Review = "services-review",
  Services_Review_Problems = "services-review-problems",
  Commers_Keywords_List = "commers-keywords-list",
  Commers_RecommendedKeywords = "commers-recommended-keywords",
  Services_Competitors_Main = "services-competitors-main",
  Services_Competitors_Product = "services-competitors-product",
  Services_SaleChannels_Overview = "services-sale-channels-overview",
  Services_SaleChannels_Marketplace = "services-sale-channels-marketplace",
}

export interface TableSettings {
  columnVisibility: VisibilityState;
  columnOrder: string[];
  date: Date;
  dateRange: DatePickerRange;
}

export const TABLES_QUERY_KEY = ["tables"] as const;

interface TableState {
  tables: {
    [key in TableIds]?: Omit<TableSettings, "date" | "dateRange">;
  };
  dateRanges: {
    [key in TableIds]?: DatePickerRange;
  };
  dates: {
    [key in TableIds]?: Date;
  };
}

interface TableActions {
  setTableSettings: (tableId: TableIds, settings: TableSettings) => void;
  getTableSettings: (tableId: TableIds) => TableSettings;
  setDateRange: (tableId: TableIds, dateRange: DatePickerRange) => void;
  getDateRange: (tableId: TableIds) => DatePickerRange;
  setDate: (tableId: TableIds, date: Date) => void;
  getDate: (tableId: TableIds) => Date;
}

type TableStore = TableState & TableActions;

// Начальные значения для дат
const getDefaultDateRange = (): DatePickerRange => ({
  from: startOfDay(subWeeks(new Date(), 1)),
  to: startOfDay(new Date()),
});

const getDefaultDate = (): Date => startOfDay(new Date());

// Начальные настройки таблицы (без дат)
const initialTableSettings: Omit<TableSettings, "date" | "dateRange"> = {
  columnOrder: [],
  columnVisibility: {},
};

export const useTableStore = create<TableStore>()(
  persist(
    (set, get) => ({
      tables: {},
      dateRanges: {},
      dates: {},

      setTableSettings: (tableId, settings) => {
        const { date, dateRange, ...restSettings } = settings;

        set((state) => ({
          tables: {
            ...state.tables,
            [tableId]: restSettings,
          },
        }));

        // Если переданы даты, обновляем их отдельно
        if (date) {
          get().setDate(tableId, date);
        }

        if (dateRange) {
          get().setDateRange(tableId, dateRange);
        }
      },

      getTableSettings: (tableId) => {
        const tableSettings = get().tables[tableId] || initialTableSettings;
        const dateRange = get().getDateRange(tableId);
        const date = get().getDate(tableId);

        return {
          ...tableSettings,
          dateRange,
          date,
        };
      },

      // Методы для работы с диапазоном дат
      setDateRange: (tableId, dateRange) => {
        set((state) => ({
          dateRanges: {
            ...state.dateRanges,
            [tableId]: dateRange,
          },
        }));
      },

      getDateRange: (tableId) => {
        return get().dateRanges[tableId] || getDefaultDateRange();
      },

      // Методы для работы с одиночной датой
      setDate: (tableId, date) => {
        set((state) => ({
          dates: {
            ...state.dates,
            [tableId]: date,
          },
        }));
      },

      getDate: (tableId) => {
        return get().dates[tableId] || getDefaultDate();
      },
    }),
    {
      name: "table-settings-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        tables: state.tables,
      }),
      version: 20,
      migrate(persistedState, version) {
        if (version !== 0) {
          return {
            tables: {},
            version: 0,
          };
        }
        return persistedState;
      },
    }
  )
);
